import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import {useSearchParams} from 'react-router';

export type HelpPlatform = 'web' | 'mobile';

const STORAGE_KEY = 'smartmixin-help-platform';

const isHelpPlatform = (value: unknown): value is HelpPlatform =>
  value === 'web' || value === 'mobile';

type HelpPlatformContextValue = {
  platform: HelpPlatform;
  setPlatform: (platform: HelpPlatform) => void;
};

const HelpPlatformContext = createContext<HelpPlatformContextValue>({
  platform: 'web',
  setPlatform: () => {},
});

// One selected platform for every tab group in the help center. Initialized
// from ?platform= (the mobile app opens help with ?platform=mobile), then
// from the reader's last explicit choice, defaulting to web.
export const HelpPlatformProvider = ({children}: {children: ReactNode}) => {
  const [searchParams] = useSearchParams();

  const [platform, setPlatformState] = useState<HelpPlatform>(() => {
    const fromQuery = searchParams.get('platform');
    if (isHelpPlatform(fromQuery)) {
      return fromQuery;
    }
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (isHelpPlatform(stored)) {
        return stored;
      }
    } catch {
      // storage unavailable (private mode) — fall through to default
    }
    return 'web';
  });

  const setPlatform = useCallback((value: HelpPlatform) => {
    setPlatformState(value);
    try {
      localStorage.setItem(STORAGE_KEY, value);
    } catch {
      // storage unavailable — selection still applies for this visit
    }
  }, []);

  const value = useMemo(
    () => ({platform, setPlatform}),
    [platform, setPlatform],
  );

  return (
    <HelpPlatformContext.Provider value={value}>
      {children}
    </HelpPlatformContext.Provider>
  );
};

export const useHelpPlatform = () => useContext(HelpPlatformContext);
